<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="text-center">
        <p class="mb-0" v-if="canCreateAJointPlan">{{ $t('md-text-2-1') }}</p>
        <p class="mb-0" v-if="canCreateAJointPlan || paidDebts.length > 0">{{ $t('md-text-2-2') }}</p>
        <p class="mb-0" v-if="paidDebts.length > 0">{{ $t('md-text-2-3') }}</p>
      </v-col>
    </v-row>
    <v-row v-if="showTabs">
      <v-col cols="12">
        <v-tabs v-model="activeTab" centered fixed slider-color="color-new-link-blue" background-color="transparent" id="frontPageTabs" @change="tabPageEvent">
          <v-tab href="#tab-paymentplan" class="text-subtitle-1 color-new-link-blue--text" v-if="canCreateAJointPlan">
            {{ $t('createPaymentPlan') }}
          </v-tab>
          <v-tab href="#tab-debts" class="text-subtitle-1 color-new-link-blue--text">
            {{ $t('md-text-4') }}
          </v-tab>
          <v-tab href="#tab-archive" class="text-subtitle-1 color-new-link-blue--text" v-if="paidDebts.length > 0">
            {{ $t('md-text-5') }}
          </v-tab>
          <v-tabs-items v-model="activeTab" touchless style="background-color: transparent;">
            <v-tab-item value="tab-paymentplan" v-if="canCreateAJointPlan">
              <v-container fluid class="px-1">
                <v-row>
                  <v-col cols="12">
                    <multi-payment-plan :debts="debtsAvailableForPaymentPlan"></multi-payment-plan>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item value="tab-debts">
              <v-container fluid class="px-1">
                <v-row v-if="summary.count > 2">
                  <v-col cols="12">
                    <div class="text-center">{{ $t('md-text-8') }} {{ summary.count }} {{ $t('kpl') }}</div>
                  </v-col>
                </v-row>
                <v-row v-if="debtsWithoutPaymentPlan.length > 0">
                  <v-col cols="12" v-if="paymentPlans.length > 0">
                    <h3 class="text-h3">{{ $t('md-text-6') }}</h3>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-for="debt in debtsWithoutPaymentPlan" :key="debt.orderNr">
                    <debt-card :debt="debt"></debt-card>
                  </v-col>
                </v-row>
                <v-row v-if="paymentPlans.length > 0">
                  <v-col cols="12" v-if="debtsWithoutPaymentPlan.length > 0">
                    <h3 class="text-h3">{{ $t('md-text-7') }}</h3>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-for="paymentPlan in paymentPlans" :key="paymentPlan.nr">
                    <payment-plan-card :paymentPlan="paymentPlan"></payment-plan-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <v-tab-item value="tab-archive" v-if="paidDebts.length > 0">
              <v-container fluid class="px-1">
                <v-row>
                  <v-col cols="12" sm="6" md="4" v-for="debt in paidDebts" :key="debt.orderNr">
                    <archive-card :debt="debt"></archive-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </v-col>
    </v-row>
    <v-row v-if="!showTabs && hasOpenDebts">
      <v-container fluid class="px-0">
        <v-row v-if="debtsWithoutPaymentPlan.length > 0">
          <v-col cols="12" v-if="paymentPlans.length > 0">
            <h3 class="text-h3">{{ $t('md-text-6') }}</h3>
          </v-col>
          <v-col cols="12" sm="6" md="4" v-for="debt in debtsWithoutPaymentPlan" :key="debt.orderNr">
            <debt-card :debt="debt"></debt-card>
          </v-col>
        </v-row>
        <v-row v-if="paymentPlans.length > 0">
          <v-col cols="12" v-if="debtsWithoutPaymentPlan.length > 0">
            <h3 class="text-h3">{{ $t('md-text-7') }}</h3>
          </v-col>
          <v-col cols="12" sm="6" md="4" v-for="paymentPlan in paymentPlans" :key="paymentPlan.nr">
            <payment-plan-card :paymentPlan="paymentPlan"></payment-plan-card>
          </v-col>
        </v-row>
      </v-container>
    </v-row>
    <v-row v-if="!showTabs && !hasOpenDebts && hasPaidDebts">
      <v-container fluid class="px-0">
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="debt in paidDebts" :key="debt.orderNr">
            <archive-card :debt="debt"></archive-card>
          </v-col>
        </v-row>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import DebtCard from './DebtCard.vue'
  import PaymentPlanCard from './PaymentPlanCard.vue'
  import ArchiveCard from './ArchiveCard.vue'
  import CreateMultiPaymentPlan from './CreateMultiPaymentPlan.vue'

  export default {
    data: function () {
      return {
        activeTab: 'tab-paymentplan'
      }
    },
    mounted () {
      if (!this.canCreateAJointPlan && this.activeTab === 'tab-paymentplan') {
        this.activeTab = 'tab-debts'
      }
    },
    computed: {
      ...mapGetters({
        debtsWithoutPaymentPlan: 'openDebtsWithoutPaymentPlan',
        paymentPlans: 'paymentPlans',
        paidDebts: 'paidDebts',
        summary: 'summary',
        hasOnlyOneDebt: 'hasOnlyOneDebt',
        canCreateAJointPlan: 'canCreateAJointPlan',
        stateActiveTab: 'frontPageActiveTab'
      }),
      debtsAvailableForPaymentPlan () {
        let availableDebts = []
        this.debtsWithoutPaymentPlan.forEach((debt) => {
          if (debt.paymentPlanParameters.allow) {
            availableDebts.push(debt)
          }
        })
        return availableDebts
      },
      showTabs () {
        if (this.canCreateAJointPlan || (this.hasPaidDebts && this.hasOpenDebts)) {
          return true
        } else {
          return false
        }
      },
      hasPaidDebts () {
        return this.paidDebts.length > 0
      },
      hasOpenDebts () {
        if (this.paymentPlans.length > 0 || this.debtsWithoutPaymentPlan.length > 0) {
          return true
        } else {
          return false
        }
      }
    },
    watch: {
      stateActiveTab: function (val) {
        this.activeTab = val
      },
      activeTab: function (val) {
        this.$store.commit('setFrontPageActiveTab', val)
      }
    },
    components: {
      'debt-card': DebtCard,
      'payment-plan-card': PaymentPlanCard,
      'archive-card': ArchiveCard,
      'multi-payment-plan': CreateMultiPaymentPlan
    }
  }
</script>

<i18n>
  {
  "fi": {
  "md-text-1": "Velkakohtaiset tiedot",
  "md-text-2-1": "Pääset helposti alkuun velkojesi hoidossa yhdistämällä kaikki velkasi samaan maksusuunnitelmaan.",
  "md-text-2-2": "Tarvittaessa voit myös lyhentää yksittäisiä velkoja haluamillasi summilla, tai kuitata velat kertamaksulla.",
  "md-text-2-3": "Arkistosta voit tarkastella jo maksettuja tai lopetettuja velkoja.",
  "md-text-4": "Hoida yksittäisiä velkoja",
  "md-text-5": "Arkisto",
  "md-text-6": "Yksittäiset velat",
  "md-text-7": "Maksusuunnitelmat",
  "md-text-8": "Velkoja"
  },
  "se": {
  "md-text-1": "Skuldinformation",
  "md-text-2-1": "Du kan enkelt kombinera dina skulder till en betalningsplan.",
  "md-text-2-2": "Du kan även betala enskilda skulder, eller betala alla skulder på en gång.",
  "md-text-2-3": "Arkivet innehåller redan betalda och avslutade ärenden.",
  "md-text-4": "Hantera enskilda skulder",
  "md-text-5": "Arkiv",
  "md-text-6": "Enskilda skulder",
  "md-text-7": "Betalningsplaner",
  "md-text-8": "Skulder"
  },
  "en": {
  "md-text-1": "Debt details",
  "md-text-2-1": "You can easily start managing your debts by combining them into one payment plan.",
  "md-text-2-2": "If needed you can also pay single debts, or pay a voluntary amount, or pay all debts at once.",
  "md-text-2-3": "The archive contains paid and closed cases. ",
  "md-text-4": "Manage single debts",
  "md-text-5": "Archive",
  "md-text-6": "Single debts",
  "md-text-7": "Payment plans",
  "md-text-8": "Debts"
  }
  }
</i18n>
